"use client"
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import Protected from '@/components/Protected';
import { Project, QuickAction } from '@/interfaces/dashboard';
import { PROJECT_FORM_FIELDS, TABLE_HEADERS } from '@/components/dashboard/constant';
import ProjectRow from '@/components/dashboard/ProjectsInRows';
import DynamicForm from '@/components/dashboard/DynamicForm';
import Modal from '@/UI/CenterScreenModal';
import Sidebar from '@/components/SideBar';
import { FilePlus, Sparkles, FileText, Database } from "lucide-react";
import ImportDatabaseModal from '@/components/dashboard/ImportDatabaseModal';


interface QuickActionCardProps {
  icon: ReactNode;
  label: string;
  tooltip: string;
  onClick: () => void;
}

const QuickActionCard: React.FC<QuickActionCardProps> = ({ icon, label, tooltip, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      title={tooltip}
      disabled={tooltip === "Comming Soon"}
      onClick={onClick}
      className="p-4 cursor-pointer text-[var(--secondaryText)] flex flex-col items-center justify-center rounded bg-[var(--hoverBackground)] hover:bg-[var(--primaryBackground)] hover:text-[var(--primaryText)] transition-all min-w-[14rem]"
      style={{
        border: `.5px solid ${isHovered ? "var(--primaryText)" : "var(--secondaryText)"
          }`,
        color: isHovered ? "var(--primaryText)" : "var(--secondaryText)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="w-16 h-16 mb-3 flex items-center justify-center rounded-lg">
        <span className="text-2xl">{icon}</span>
      </div>
      <h3 className="text-sm font-medium  text-center">{label}</h3>
    </button>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [formValues, setFormValues] = useState<Record<string, string>>({
    name: '',
    description: '',
  });
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [error, setError] = useState('');
  const [projectLoading, setProjectLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchProjects();
    }
  }, [user]);

  const fetchProjects = async () => {
    setProjectLoading(true);
    try {
      const res = await fetch('/api/projects');
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      } else {
        setError('Failed to fetch projects');
      }
    } catch (err) {
      setError('Failed to fetch projects');
      console.error(err);
    } finally {
      setProjectLoading(false);
    }
  };

  const handleFormChange = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formValues.name.trim()) {
      setError('Project name cannot be empty');
      return;
    }

    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues),
      });

      if (res.ok) {
        setFormValues({ name: '', description: '' });
        setIsCreateOpen(false);
        fetchProjects();
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to create project');
      }
    } catch (err) {
      setError('Failed to create project');
      console.error(err);
    }
  };

  const handleDeleteProject = async (id: string) => {
    if (!confirm('Delete this project?')) return;
    try {
      const res = await fetch(`/api/projects/${id}`, { method: 'DELETE' });
      if (res.ok) fetchProjects();
    } catch (err) {
      console.error('Failed to delete project', err);
    }
  };

  const handleShareProject = async (id: string) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/workspace/${id}`);
      return true;
    } catch (err) {
      console.error('Failed to copy link', err);
      return false;
    }
  };

  const quickActions: QuickAction[] = [
    {
      id: "blank",
      icon: <FilePlus className="w-16 h-16" strokeWidth={1} />,
      label: "Create a Blank File",
      tooltip: "Create a new empty project",
      onClick: () => setIsCreateOpen(true)
    },
    {
      id: "import-db",
      icon: <Database className="w-16 h-16" strokeWidth={1} />,
      label: "Import from Database",
      tooltip: "Create project from existing database",
      onClick: () => setIsImportOpen(true)
    },
    {
      id: "diagram",
      icon: <Sparkles className="w-16 h-16 " strokeWidth={1} />,
      label: "Generate an AI Diagram",
      tooltip: "Comming Soon",
      onClick: () => console.log("AI Diagram")
    },
  ];

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [projects]);

  return (
    <Protected>
      <div className="flex h-screen bg-background">
        <Sidebar />

        <main className="flex-1 overflow-auto">
          <div className="p-8">
            {/* Quick Actions */}
            <div className="flex gap-6 mb-8">
              {quickActions.map((action) => (
                <QuickActionCard key={action.id} {...action} tooltip={action.tooltip || ""} />
              ))}
            </div>

            {/* Projects Table */}
            <div className="rounded-xl">
              <div className="p-2">

                {projectLoading ? (
                  <p className="text-[var(--primaryText)]">Loading projects...</p>
                ) : projects.length === 0 ? (
                  <p className="text-[var(--primaryText)]">No projects found. Create one above!</p>
                ) : (
                  <div className="">
                    <table className="min-w-full text-left ">
                      <thead>
                        <tr className="border-b border-primaryBorder">
                          {TABLE_HEADERS.map((header) => (
                            <th
                              key={header.key}
                              className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--primaryText)] text-${header.align}`}
                            >
                              {header.label}
                              {header.sortable && <span className="ml-1">↕</span>}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedProjects.map((project, idx) => (
                          <ProjectRow
                            key={project._id}
                            project={project}
                            onEdit={(id) => window.location.href = `/workspace/${id}`}
                            onDelete={handleDeleteProject}
                            onShare={handleShareProject}
                            isLast={idx === sortedProjects.length - 1}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Create Project Modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Create New Project">
        <DynamicForm
          fields={PROJECT_FORM_FIELDS}
          values={formValues}
          onChange={handleFormChange}
          onSubmit={handleCreateProject}
          error={error}
          submitLabel="Create"
          onCancel={() => setIsCreateOpen(false)}
        />
      </Modal>

      {/* Import Database Modal */}
      <ImportDatabaseModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onSuccess={() => {
          setIsImportOpen(false);
          fetchProjects();
        }}
      />
    </Protected>
  );
};

export default DashboardPage;